import * as amplitude from "@amplitude/analytics-browser";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  CloseButton,
  Flex,
} from "@chakra-ui/react";
import React from "react";
import { AnimatedProgress } from "src/components/Feedback/AnimatedProgress";
import {
  getBulkActionProgress,
  type BulkActionProgress,
} from "src/generated/hooks/form-service/requests";
import { WEGLOT_APPLY_CLASS } from "src/plugins/weglot/constants";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import type { BulkActionType } from "../activity/labels";
import {
  BULK_ACTION_PROGRESS_POLL_MS,
  BULK_ACTION_PROGRESS_TOAST_COMPLETE_MS,
  computeBulkActionProgress,
  getBulkActionProgressToastCopy,
} from "./bulkActionProgress";
import { trackFormAction } from "./trackFormAction";

export type BulkActionProgressToastInput = {
  bulkActionId: string;
  formTemplateId: uuid;
  actionType: BulkActionType;
  selectionSize: number;
};

type BulkActionProgressToastViewProps = BulkActionProgressToastInput & {
  bulkAction?: BulkActionProgress;
  organization: GQL.Organization;
  onClose: () => void;
};

export const BulkActionProgressToastView: React.FC<
  BulkActionProgressToastViewProps
> = ({
  bulkActionId,
  formTemplateId,
  actionType,
  selectionSize,
  bulkAction,
  organization,
  onClose,
}) => {
  const progress = computeBulkActionProgress(bulkAction, selectionSize);
  const copy = getBulkActionProgressToastCopy(actionType, progress);
  const hasErrors = progress.errorCount > 0;
  const alertStatus = progress.isComplete
    ? hasErrors
      ? "error"
      : "success"
    : "info";

  const activityHref = Url.OrgAdmin.Forms.activityDetail({
    organization,
    formTemplateId,
    bulkActionId,
    params: hasErrors ? "status=FAILED" : undefined,
  });

  return (
    <Alert
      status={alertStatus}
      variant="solid"
      borderRadius="md"
      alignItems="flex-start"
      maxW="sm"
      pr={3}
      width="100%"
    >
      <AlertIcon mt={0.5} />
      <Box flex="1" minW={0} mr={2}>
        <AlertTitle className={WEGLOT_APPLY_CLASS} fontSize="md">
          {copy.title}
        </AlertTitle>
        <AlertDescription
          className={WEGLOT_APPLY_CLASS}
          display="block"
          fontSize="sm"
          opacity={0.95}
        >
          {copy.description}
        </AlertDescription>
        {!progress.isComplete && (
          <AnimatedProgress
            aria-label="Bulk action progress"
            mt={3}
            size="sm"
            borderRadius="full"
            bg="whiteAlpha.400"
            isIndeterminate={progress.processed === 0}
            value={progress.progressPercent}
            sx={{ "& > div:first-of-type": { bg: "white" } }}
          />
        )}
      </Box>
      {progress.isComplete ? (
        <Flex align="center" alignSelf="center" gap={1}>
          <Button
            as="a"
            href={activityHref}
            variant="link"
            color="white"
            fontSize="sm"
            fontWeight="bold"
            textTransform="uppercase"
            _hover={{ textDecoration: "underline" }}
            onClick={() => {
              amplitude.track("Clicked bulk activity detail", {
                action_type: actionType,
                bulk_action_id: bulkActionId,
                source: "toast",
                has_errors: hasErrors,
              });
            }}
          >
            View
          </Button>
          <CloseButton color="white" onClick={onClose} size="sm" />
        </Flex>
      ) : (
        <CloseButton
          alignSelf="flex-start"
          color="white"
          onClick={onClose}
          size="sm"
        />
      )}
    </Alert>
  );
};

export const BulkActionProgressToastPoller: React.FC<
  BulkActionProgressToastInput & {
    organization: GQL.Organization;
    onClose: () => void;
    onComplete?: () => void;
  }
> = (props) => {
  const { bulkActionId, actionType, selectionSize, onComplete } = props;
  const [bulkAction, setBulkAction] = React.useState<
    BulkActionProgress | undefined
  >(undefined);
  const completedTracked = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const response = await getBulkActionProgress({
          path: { bulkActionId },
          throwOnError: false,
        });
        const next = response.data?.data;
        if (cancelled) {
          return;
        }
        if (next?.status === "DONE" && !completedTracked.current) {
          completedTracked.current = true;
          const progress = computeBulkActionProgress(next, selectionSize);
          trackFormAction({
            verb: "Completed",
            actionType,
            bulk: true,
            count: selectionSize,
            async: true,
            extra: {
              done_count: progress.doneCount,
              error_count: next.errorCount ?? 0,
              bulk_action_id: bulkActionId,
            },
          });
          onComplete?.();
        }
        if (next) {
          setBulkAction(next);
        }
        if (next?.status === "DONE") {
          return;
        }
        timeoutId = setTimeout(poll, BULK_ACTION_PROGRESS_POLL_MS);
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error(error);
        timeoutId = setTimeout(poll, BULK_ACTION_PROGRESS_POLL_MS);
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [actionType, bulkActionId, onComplete, selectionSize]);

  React.useEffect(() => {
    if (bulkAction?.status !== "DONE") {
      return;
    }
    const timeoutId = setTimeout(
      props.onClose,
      BULK_ACTION_PROGRESS_TOAST_COMPLETE_MS
    );
    return () => clearTimeout(timeoutId);
  }, [bulkAction?.status, props.onClose]);

  return <BulkActionProgressToastView {...props} bulkAction={bulkAction} />;
};
